import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'
import {useEffect, useRef, useState} from 'react'
import {
  DEFAULT_SIGNAL_CONFIG,
  SIGNAL_CONFIG_PATH,
  SignalConfig,
  getSignalConfigStatus,
  loadSignalConfig,
  parseSignalConfig,
  stringifySignalConfig,
} from 'utils/config/signalConfig'
import {signalGradeEnum} from 'utils/const/globalConst'
import {normalAlert, resultAlert} from 'utils/CustomAlert/Alert'

/** 칸을 누를 때마다 이 순서로 순환한다 */
const gradeCycle = [signalGradeEnum.GOOD, signalGradeEnum.NORMAL, signalGradeEnum.NEED_CHECK]

/** nginx .htpasswd 에 등록한 계정명. 비밀번호는 코드에 두지 않는다. */
const CONFIG_AUTH_USER = 'admin'

const gradeColor = (grade: signalGradeEnum) => {
  switch (grade) {
    case signalGradeEnum.GOOD:
      return {backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7'}
    case signalGradeEnum.NORMAL:
      return {backgroundColor: '#fff8e1', color: '#f57c00', border: '1px solid #ffe082'}
    default:
      return {backgroundColor: '#ffebee', color: '#d32f2f', border: '1px solid #ef9a9a'}
  }
}

/** 경계 배열로 각 구간의 표시 문구를 만든다. (≥-90 / -90~-100 / ... / <-110) */
const rangeLabels = (thresholds: number[]) => [
  `≥${thresholds[0]}`,
  ...thresholds.slice(1).map((t, i) => `${thresholds[i]}~${t}`),
  `<${thresholds[thresholds.length - 1]}`,
]

const SignalConfigPage = () => {
  const [config, setConfig] = useState<SignalConfig>(DEFAULT_SIGNAL_CONFIG)
  const [status, setStatus] = useState(getSignalConfigStatus())
  const [saving, setSaving] = useState(false)
  const [authOpen, setAuthOpen] = useState(false)
  const [password, setPassword] = useState('')
  /* 저장은 nginx Basic 인증을 통과해야 한다.
     비밀번호를 코드나 localStorage 에 두면 인증 의미가 없으므로,
     저장할 때 입력받아 같은 화면에 머무는 동안만 메모리에 들고 있는다. */
  const credentialRef = useRef<string | null>(null)

  const refresh = async () => {
    const loaded = await loadSignalConfig()
    setConfig({
      rsrpThresholds: [...loaded.rsrpThresholds],
      rsrqThresholds: [...loaded.rsrqThresholds],
      matrix: loaded.matrix.map((row) => [...row]),
    })
    setStatus(getSignalConfigStatus())
  }

  useEffect(() => {
    refresh()
  }, [])

  const updateThreshold = (axis: 'rsrpThresholds' | 'rsrqThresholds', index: number, raw: string) => {
    const next = [...config[axis]]
    next[index] = Number(raw)
    setConfig({...config, [axis]: next})
  }

  const cycleCell = (row: number, col: number) => {
    const nextMatrix = config.matrix.map((cells) => [...cells])
    const current = gradeCycle.indexOf(nextMatrix[row][col])
    nextMatrix[row][col] = gradeCycle[(current + 1) % gradeCycle.length]
    setConfig({...config, matrix: nextMatrix})
  }

  /** 저장 전 검증. 화면에서 만든 값이라도 경계 역전 같은 실수는 걸러야 한다. */
  const validate = (): string | null => {
    const parsed = parseSignalConfig(JSON.parse(stringifySignalConfig(config)))
    return typeof parsed === 'string' ? parsed : null
  }

  const download = () => {
    const error = validate()
    if (error) {
      normalAlert(error)
      return
    }
    const blob = new Blob([stringifySignalConfig(config)], {type: 'application/json'})
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'signal-config.json'
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  /** 저장 버튼. 비밀번호가 아직 없으면 먼저 입력받는다. */
  const requestSave = () => {
    const error = validate()
    if (error) {
      normalAlert(error)
      return
    }
    if (!credentialRef.current) {
      setAuthOpen(true)
      return
    }
    save()
  }

  const confirmPassword = () => {
    try {
      credentialRef.current = btoa(`${CONFIG_AUTH_USER}:${password}`)
    } catch {
      /* btoa 는 한글 등 비 ASCII 를 처리하지 못한다 */
      normalAlert('비밀번호는 영문·숫자·기호만 사용할 수 있습니다.')
      return
    }
    setPassword('')
    setAuthOpen(false)
    save()
  }

  const save = async () => {
    setSaving(true)
    try {
      const res = await fetch(SIGNAL_CONFIG_PATH, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${credentialRef.current}`,
        },
        body: stringifySignalConfig(config),
      })

      if (res.status === 401 || res.status === 403) {
        /* 잘못된 비밀번호를 계속 들고 있으면 다음 저장도 막히므로 버린다 */
        credentialRef.current = null
        await normalAlert('비밀번호가 올바르지 않습니다. 다시 시도해주세요.')
        return
      }
      if (!res.ok) {
        await normalAlert(
          `저장하지 못했습니다. (HTTP ${res.status})\n서버에 쓰기 설정이 되어 있는지 확인해주세요.`
        )
        return
      }

      /* PUT 응답만 믿지 않고 다시 읽어 실제 반영을 확인한다 */
      await refresh()
      await resultAlert({resultCode: 0})
    } catch {
      await normalAlert('저장 요청에 실패했습니다. 네트워크 상태를 확인해주세요.')
    } finally {
      setSaving(false)
    }
  }

  const rsrpLabels = rangeLabels(config.rsrpThresholds)
  const rsrqLabels = rangeLabels(config.rsrqThresholds)

  return (
    <div className={'card card-body'}>
      {/* 서버에 직접 접속할 수 없는 환경이라, 지금 무엇이 적용 중인지 화면이 말해줘야 한다 */}
      {status.source === 'remote' ? (
        <Alert severity={'success'} className={'mb-4 fs-5'}>
          설정 파일이 적용 중입니다.
          {status.loadedAt && ` (확인 시각 ${status.loadedAt.toLocaleString('ko-KR')})`}
        </Alert>
      ) : (
        <Alert severity={'warning'} className={'mb-4 fs-5'}>
          기본값으로 동작 중입니다. {status.error ?? '설정 파일이 없습니다.'}
        </Alert>
      )}

      <div className={'mb-5'}>
        <h3 className={'fw-bold fs-4 mb-3'}>전파 세기(RSRP) 임계값 (dBm)</h3>
        <div className={'d-flex flex-wrap gap-3'}>
          {config.rsrpThresholds.map((value, index) => (
            <TextField
              key={`rsrp-${index}`}
              type={'number'}
              size={'small'}
              value={value}
              onChange={(e) => updateThreshold('rsrpThresholds', index, e.target.value)}
            />
          ))}
        </div>
      </div>

      <div className={'mb-5'}>
        <h3 className={'fw-bold fs-4 mb-3'}>전파 품질(RSRQ) 임계값 (dB)</h3>
        <div className={'d-flex flex-wrap gap-3'}>
          {config.rsrqThresholds.map((value, index) => (
            <TextField
              key={`rsrq-${index}`}
              type={'number'}
              size={'small'}
              value={value}
              onChange={(e) => updateThreshold('rsrqThresholds', index, e.target.value)}
            />
          ))}
        </div>
      </div>

      <div className={'mb-5'}>
        <h3 className={'fw-bold fs-4 mb-1'}>분류 매트릭스</h3>
        <p className={'text-muted fs-6'}>
          각 칸을 누르면 양호 → 보통 → 확인필요 순으로 바뀝니다.
        </p>
        {/* 열이 많아지면 좁은 화면에서 넘치므로 표만 가로 스크롤한다 */}
        <div style={{overflowX: 'auto'}}>
          <table className={'table table-bordered align-middle text-center mb-0'}>
            <thead>
              <tr>
                <th style={{whiteSpace: 'nowrap'}}>RSRQ ＼ RSRP</th>
                {rsrpLabels.map((label) => (
                  <th key={label} style={{whiteSpace: 'nowrap'}}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {config.matrix.map((cells, row) => (
                <tr key={`row-${row}`}>
                  <th style={{whiteSpace: 'nowrap'}}>{rsrqLabels[row]}</th>
                  {cells.map((grade, col) => (
                    <td key={`cell-${row}-${col}`} style={{padding: 4}}>
                      <Chip
                        label={grade}
                        onClick={() => cycleCell(row, col)}
                        sx={{...gradeColor(grade), fontWeight: 'bold', width: '100%'}}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={'d-flex flex-wrap gap-3'}>
        <Button variant={'contained'} onClick={requestSave} disabled={saving}>
          {saving ? '저장 중...' : '저장'}
        </Button>
        <Button variant={'outlined'} onClick={download}>
          JSON 다운로드
        </Button>
        <Button variant={'outlined'} color={'inherit'} onClick={refresh}>
          다시 불러오기
        </Button>
        <Button
          variant={'text'}
          color={'inherit'}
          onClick={() =>
            setConfig({
              rsrpThresholds: [...DEFAULT_SIGNAL_CONFIG.rsrpThresholds],
              rsrqThresholds: [...DEFAULT_SIGNAL_CONFIG.rsrqThresholds],
              matrix: DEFAULT_SIGNAL_CONFIG.matrix.map((row) => [...row]),
            })
          }
        >
          기본값으로 되돌리기
        </Button>
      </div>

      <Dialog open={authOpen} onClose={() => setAuthOpen(false)} fullWidth maxWidth={'xs'}>
        <DialogTitle className={'fw-bold'}>저장 비밀번호</DialogTitle>
        <DialogContent>
          <p className={'text-muted fs-6 mb-3'}>
            설정을 저장하려면 비밀번호가 필요합니다. 이 화면을 벗어나면 다시 입력해야 합니다.
          </p>
          <TextField
            autoFocus
            fullWidth
            type={'password'}
            label={'비밀번호'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmPassword()
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button color={'inherit'} onClick={() => setAuthOpen(false)}>
            취소
          </Button>
          <Button variant={'contained'} onClick={confirmPassword} disabled={password === ''}>
            저장
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default SignalConfigPage
